import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist'
import type * as PdfJs from 'pdfjs-dist/legacy/build/pdf.mjs'

// pdf.js, chargé à la demande : les aperçus de pièces sont dessinés dans l'appli, pareil sur
// ordinateur et dans la PWA (un PDF dans un cadre ne s'affiche pas sur Android, et un onglet
// ouvert hors de l'appli n'a pas la session sur iPhone). Sans WebAssembly (CSP) : les
// décodeurs d'images des scans passent par leur version JavaScript, servie sous /pdfjs/.
// Version « legacy » : la courante suppose des API trop récentes (Map.getOrInsertComputed)
// pour Safari sur iPhone et bien des navigateurs encore en service.

let chargement: Promise<typeof PdfJs> | null = null

function pdfjs() {
  chargement ??= Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
  ]).then(([m, travailleur]) => {
    m.GlobalWorkerOptions.workerSrc = travailleur.default
    return m
  })
  return chargement
}

/**
 * Ouvre le fichier d'une pièce (même origine : la session suit). `fermer` libère le document
 * et son chargement, même en cours.
 */
export function ouvrirPdf(url: string) {
  let tache: PDFDocumentLoadingTask | null = null
  let ferme = false
  const doc = pdfjs().then((m) => {
    if (ferme) throw new Error('Fermé.')
    tache = m.getDocument({
      url,
      useWasm: false,
      wasmUrl: '/pdfjs/wasm/',
      standardFontDataUrl: '/pdfjs/standard_fonts/',
    })
    return tache.promise
  })
  return {
    doc,
    fermer: () => {
      ferme = true
      void tache?.destroy()
    },
  }
}

/**
 * Dessine une page à la largeur voulue (en pixels CSS), nette sur les écrans denses.
 * `annuler` arrête le rendu (démontage, nouvelle largeur) : un canevas, un rendu à la fois.
 */
export function dessinerPage(
  doc: PDFDocumentProxy,
  numero: number,
  canvas: HTMLCanvasElement,
  largeur: number,
) {
  let annule = false
  let tache: RenderTask | null = null
  const promesse = doc.getPage(numero).then(async (page) => {
    if (annule) return
    const naturelle = page.getViewport({ scale: 1 })
    const densite = Math.min(window.devicePixelRatio || 1, 2)
    const viewport = page.getViewport({ scale: (largeur / naturelle.width) * densite })
    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    tache = page.render({ canvas, viewport })
    await tache.promise
  })
  return {
    promesse,
    annuler: () => {
      annule = true
      tache?.cancel()
    },
  }
}
